import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@spz/ui/components/form";
import { SearchableCheckboxTreeField } from "@spz/ui/components/searchable-checkbox-tree-field";
import { Section } from "@spz/ui/components/section";
import { Textarea } from "@spz/ui/components/textarea";
import { TextField } from "@spz/ui/components/textfield";
import { cn } from "@spz/ui/lib/utils";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import { selectCompanyGroupFormViewModel } from "./company-group-form.view-model";

interface CompanyGroupFormProps {
  groupId?: string;
  className?: string;
  onSuccess?: () => void;
  ref?: Ref<HTMLFormElement>;
}

export const CompanyGroupForm = ({
  className,
  groupId,
  onSuccess,
  ref,
}: CompanyGroupFormProps) => {
  const { t } = useTranslation();
  const { form, onSubmit, companies } = selectCompanyGroupFormViewModel({
    groupId,
    onSuccess,
  });

  return (
    <Section
      title={groupId ? t("group_form_edit_title") : t("create_new_group")}
      description={t("company_group_form_description")}
      className={cn("gap-4 px-6 pb-6", className)}
      darken
    >
      <Form {...form}>
        <form
          ref={ref}
          onSubmit={onSubmit}
          id="company-group-form"
          className="flex flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <TextField
                    {...field}
                    placeholder={t("group_name")}
                    className="w-full"
                    floatingPlaceholder
                    helperText={t("company_group_form_name_helper_text")}
                    error={fieldState.error}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t("description")}
                    className="min-h-10 max-h-13"
                    helperText={t("company_group_form_description_helper_text")}
                    error={fieldState.error}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="companies"
            render={({ field, fieldState }) => (
              <SearchableCheckboxTreeField
                label={t("company_group_form_companies_label")}
                description={t("company_group_form_companies_description")}
                searchPlaceholder={t(
                  "company_group_form_companies_search_placeholder",
                )}
                emptyList={t("no_companies")}
                loading={companies.loading}
                options={companies.options}
                value={field.value}
                onChange={({ selectedIds }) => {
                  field.onChange(selectedIds);
                  field.onBlur();
                }}
                error={fieldState.error}
              />
            )}
          />
        </form>
      </Form>
    </Section>
  );
};
